import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Star } from "lucide-react";
import { Drawer, Box } from "@mui/material";
import styles from "./moreFilters.module.css";
import CloseIcon from '../../../../assets/images/close-icon.svg'

const DEFAULT_FAVORITES = 9;

const EMPTY_CATEGORIES = [];
const EMPTY_ITEM_OPTIONS = {};

const drawerPaperSx = {
    background: "transparent",
    boxShadow: "none",
    overflow: "visible",
    width: "auto",
    maxWidth: "100%",
};

const optionValue = (item) => (
    typeof item === "object" && item !== null ? (item.value ?? item.label) : item
);

function MoreFilters({
    open,
    onClose,
    onApply,
    apiFilters,
    favoriteKeys,
    favoriteLimit,
    selectedValues,
}) {
    const { derivedCategories, itemOptions } = useMemo(() => {
        if (!apiFilters || typeof apiFilters !== "object") {
            return { derivedCategories: EMPTY_CATEGORIES, itemOptions: EMPTY_ITEM_OPTIONS };
        }
        const keys = Object.keys(apiFilters);
        const favSet = Array.isArray(favoriteKeys)
            ? new Set(favoriteKeys)
            : new Set(keys.slice(0, DEFAULT_FAVORITES));
        return {
            derivedCategories: keys.map((key) => ({
                key,
                label: key,
                favorite: favSet.has(key),
            })),
            itemOptions: Object.fromEntries(
                keys.map((key) => [key, Array.isArray(apiFilters[key]) ? apiFilters[key] : []])
            ),
        };
    }, [apiFilters, favoriteKeys]);

    const [categories, setCategories] = useState(derivedCategories);
    const [activeKey, setActiveKey] = useState(derivedCategories[0]?.key);
    const [selected, setSelected] = useState({});

    useEffect(() => {
        if (!open) return;
        setCategories(derivedCategories);
        setActiveKey((current) => (
            derivedCategories.some((c) => c.key === current) ? current : derivedCategories[0]?.key
        ));
        const seeded = {};
        Object.keys(itemOptions).forEach((key) => {
            const fromParent = Array.isArray(selectedValues?.[key]) ? selectedValues[key] : [];
            seeded[key] = new Set(fromParent);
        });
        setSelected(seeded);
    }, [open, derivedCategories, itemOptions, selectedValues]);

    const toggleFavorite = (key) => {
        setCategories((prev) => {
            const target = prev.find((c) => c.key === key);
            if (!target) return prev;
            if (!target.favorite && typeof favoriteLimit === "number") {
                const currentFavoriteCount = prev.reduce((n, c) => n + (c.favorite ? 1 : 0), 0);
                if (currentFavoriteCount >= favoriteLimit) return prev;
            }
            return prev.map((c) => (c.key === key ? { ...c, favorite: !c.favorite } : c));
        });
    };

    const toggleItem = (categoryKey, value) => {
        setSelected((prev) => {
            const next = { ...prev };
            const set = new Set(next[categoryKey] || []);
            if (set.has(value)) set.delete(value);
            else set.add(value);
            next[categoryKey] = set;
            return next;
        });
    };

    const items = itemOptions[activeKey] || [];
    const activeSelection = selected[activeKey] || new Set();
    const favoriteCount = categories.reduce((n, c) => n + (c.favorite ? 1 : 0), 0);
    const limitReached = typeof favoriteLimit === "number" && favoriteCount >= favoriteLimit;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            slotProps={{ paper: { sx: drawerPaperSx } }}
        >
            <aside className={styles.drawer}>
                <header className={styles.header}>
                    <h2 className={styles.title}>More Filters</h2>
                    <button className={styles.closeButton} type="button" onClick={onClose} aria-label="Close">
                        <Box src={CloseIcon} component='img' alt="Close" />
                    </button>
                </header>

                <div className={styles.body}>
                    <p className={styles.description}>
                        Explore additional filter options in this panel. Click the star icon to mark a filter as a favorite—up to 9 filters will be displayed on the page. To update your favorites, simply uncheck a starred filter and select a new one.
                    </p>

                    <h3 className={styles.sectionLabel}>Current Selection</h3>

                    <div className={styles.panes}>
                        {categories.length === 0 ? (
                            <Box className={styles.emptyState} sx={{ gridColumn: "1 / -1" }}>
                                No filter data available
                            </Box>
                        ) : (
                            <>
                                <Box className={styles.categoryListLine}>
                                    <ul className={styles.categoryList}>
                                        {categories.map((category) => {
                                            const starDisabled = !category.favorite && limitReached;
                                            return (
                                                <li
                                                    key={category.key}
                                                    className={`${styles.categoryRow} ${activeKey === category.key ? styles.categoryRowActive : ""}`}
                                                >
                                                    <button
                                                        className={`${styles.starButton} ${starDisabled ? styles.starButtonDisabled : ""}`}
                                                        type="button"
                                                        onClick={() => toggleFavorite(category.key)}
                                                        disabled={starDisabled}
                                                        aria-label={`${category.favorite ? "Unfavorite" : "Favorite"} ${category.label}`}
                                                        title={starDisabled ? `Up to ${favoriteLimit} filters can be selected` : undefined}
                                                    >
                                                        <Star
                                                            size={18}
                                                            className={category.favorite ? styles.starActive : styles.starInactive}
                                                            fill={category.favorite ? "#0066CC" : "none"}
                                                        />
                                                    </button>
                                                    <button
                                                        className={styles.categoryButton}
                                                        type="button"
                                                        onClick={() => setActiveKey(category.key)}
                                                    >
                                                        {category.label}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </Box>
                                {items.length === 0 ? (
                                    <Box className={styles.emptyState}>No data found</Box>
                                ) : (

                                    <ul className={styles.itemList}>
                                        {items.map((item, index) => {
                                            const value = optionValue(item);
                                            const label = typeof item === "object" && item !== null
                                                ? (item.label ?? item.value)
                                                : item;
                                            const checked = activeSelection.has(value);
                                            return (
                                                <li className={styles.itemRow} key={`${activeKey}-${index}-${value}`}>
                                                    <label className={styles.itemLabel}>
                                                        <input
                                                            type="checkbox"
                                                            className={styles.checkboxInput}
                                                            checked={checked}
                                                            onChange={() => toggleItem(activeKey, value)}
                                                        />
                                                        <span
                                                            className={`${styles.checkbox} ${checked ? styles.checkboxChecked : ""}`}
                                                            aria-hidden="true"
                                                        />
                                                        <span>{label}</span>
                                                    </label>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <footer className={styles.footer}>
                    <button className={styles.cancelButton} type="button" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className={styles.applyButton}
                        type="button"
                        onClick={() => {
                            const nextFavorites = categories.filter((c) => c.favorite).map((c) => c.key);
                            // Flatten Sets → plain value arrays so the parent
                            // can merge directly into the filter-bar state.
                            const nextSelectedValues = {};
                            Object.keys(itemOptions).forEach((key) => {
                                const set = selected[key];
                                nextSelectedValues[key] = set instanceof Set ? Array.from(set) : [];
                            });
                            onApply?.({
                                favoriteKeys: nextFavorites,
                                categories,
                                selected,
                                selectedValues: nextSelectedValues,
                            });
                            onClose?.();
                        }}
                    >
                        Apply
                    </button>
                </footer>
            </aside>
        </Drawer>
    );
}

MoreFilters.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onApply: PropTypes.func,
    apiFilters: PropTypes.objectOf(PropTypes.array),
    favoriteKeys: PropTypes.arrayOf(PropTypes.string),
    favoriteLimit: PropTypes.number,
    selectedValues: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))),
};

MoreFilters.defaultProps = {
    open: false,
    onClose: undefined,
    onApply: undefined,
    apiFilters: undefined,
    favoriteKeys: undefined,
    favoriteLimit: undefined,
    selectedValues: undefined,
};

export default MoreFilters;